import React, { useEffect, useState } from 'react';
import { Receipt, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { InventoryPage } from '../inventory/InventoryChrome';
import { Card, CardHeader, Kpi, Note, Empty } from '../../components/finance/financeUi';
import { money } from '../../components/finance/financeFormat';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { estateAdminApi, type FeesReport } from '../../services/estateAdminApi';

const currentMonth = (): string => new Date().toISOString().slice(0, 7);

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

const EstateFeesReportPage: React.FC = () => {
  const [month, setMonth] = useState(currentMonth());
  const [report, setReport] = useState<FeesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let off = false;
    setLoading(true);
    setError(null);
    estateAdminApi.feesReportJson(month)
      .then((r) => { if (!off) setReport(r); })
      .catch((e) => { if (!off) setError(e?.message || 'Could not load the fees report'); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [month]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const csv = await estateAdminApi.feesReportCsv(month);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ruby-estate-fees-${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'Could not download the CSV');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <InventoryPage icon={Receipt} title="Fees report" subtitle="What RubyTill earned from Ruby Estate, by business" crumb="Ruby Estate">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-600"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-stone-700 px-2 min-w-[150px] text-center">{monthLabel(month)}</span>
          <button
            type="button"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            disabled={month >= currentMonth()}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-600 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <button
          type="button"
          onClick={downloadCsv}
          disabled={downloading || !report}
          className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-black disabled:opacity-40 min-h-[44px]"
        >
          {downloading ? <RubyGemMark size={14} drawing color="currentColor" /> : <Download size={14} />}
          Download CSV
        </button>
      </div>

      {error && <Note tone="danger">{error}</Note>}

      {loading || !report ? (
        <RubyLoader variant="page" label="Loading the fees report" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Volume" value={money(report.totals.volumeCents)} />
            <Kpi label="Fees" value={money(report.totals.feeCents)} />
            <Kpi label="Payments" value={String(report.totals.count)} />
          </div>

          <Card>
            <CardHeader title="By business" />
            {report.byBusiness.length === 0 ? (
              <Empty icon={<Receipt size={28} />} title="No fee activity this month" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                      <th className="px-4 py-2.5">Business</th>
                      <th className="px-4 py-2.5">Volume</th>
                      <th className="px-4 py-2.5">Fees</th>
                      <th className="px-4 py-2.5">Payments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byBusiness.map((b) => (
                      <tr key={b.tenantId} className="border-b border-stone-100 last:border-0">
                        <td className="px-4 py-3 font-medium text-stone-800">{b.businessName || b.tenantId}</td>
                        <td className="px-4 py-3 tabular-nums text-stone-700">{money(b.volumeCents)}</td>
                        <td className="px-4 py-3 tabular-nums text-stone-700">{money(b.feeCents)}</td>
                        <td className="px-4 py-3 text-stone-600">{b.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </InventoryPage>
  );
};

export default EstateFeesReportPage;
